import { useState } from 'react';

import DnsRoundedIcon from '@mui/icons-material/DnsRounded';
import WifiRoundedIcon from '@mui/icons-material/WifiRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import WifiOffRoundedIcon from '@mui/icons-material/WifiOffRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { Box, Link, Stack, Button, Divider, Typography, CircularProgress } from '@mui/material';

import { useAppVersion } from 'src/hooks/use-app-version';

import { PLATFORM_INFO } from 'src/config-global';
import useAuthStore from 'src/auth/store/firebase/auth-store';
import { BootDiagnosticsHost } from 'src/boot/boot-diagnostics';
import supportQrCode from 'src/assets/images/qrCode-support-page.svg';
import { BOOT_DIAGNOSTICS_TRIGGER_ATTRIBUTE } from 'src/boot/boot-diagnostics-trigger';

import MuiIcon from 'src/components/mui-icon';
import logo from 'src/components/logo/logo/logo_cropped.svg';

import { useConnectivityStore } from './connectivity-store';
import { WifiConnectionDialog } from './wifi-connection-dialog';
import { connectivityCoordinator } from './connectivity-service';
import { useOfflineShellAvailable } from './offline-shell-access';
import {
  useSimulatedFirstPartyOutage,
  setSimulateFirstPartyApiOutage,
} from './connectivity-debug-overrides';

type Props = {
  applicationError?: string | null;
  supportReference?: string | null;
  onApplicationRetry?: (() => Promise<void> | void) | null;
};

export function ConnectionRecoveryPage({
  applicationError = null,
  supportReference = null,
  onApplicationRetry = null,
}: Props) {
  const appVersion = useAppVersion();
  const runtimeState = useConnectivityStore((state) => state.runtimeState);
  const issue = useConnectivityStore((state) => state.issue);
  const message = useConnectivityStore((state) => state.message);
  const checkStage = useConnectivityStore((state) => state.checkStage);
  const deviceConnected = useConnectivityStore((state) => state.deviceConnected);
  const internetReachable = useConnectivityStore((state) => state.internetReachable);
  const apiReachable = useConnectivityStore((state) => state.apiReachable);
  const authRestorationStatus = useAuthStore((state) => state.authRestorationStatus);
  const offlineShellAvailable = useOfflineShellAvailable();
  const simulatedOutage = useSimulatedFirstPartyOutage();
  const [wifiDialogOpen, setWifiDialogOpen] = useState(false);
  const [applicationRetrying, setApplicationRetrying] = useState(false);

  const checking = runtimeState === 'CHECKING' || runtimeState === 'RECOVERING';
  const deviceIssue = issue === 'device' || !deviceConnected;
  const internetIssue = issue === 'internet';
  const pointCraftUnavailableWithInternet = internetReachable === true && apiReachable === false;
  let overline = "CAN'T REACH POINTCRAFT";
  let headline = "WE'RE HAVING TROUBLE CONNECTING.";
  let description =
    'Your network is connected, but a required PointCraft request is not completing. We will keep checking safely.';
  if (deviceIssue) {
    overline = 'NO NETWORK CONNECTION';
    headline = "LET'S GET YOU BACK ONLINE.";
    description =
      'Connect this device to the internet, then PointCraft will verify its services and restore your session.';
  }
  if (internetIssue) {
    overline = 'NO INTERNET ACCESS';
    headline = "LET'S GET YOU BACK ONLINE.";
    description =
      'This device is connected to a network, but that network cannot currently reach the internet. Check the merchant Wi-Fi or internet service.';
  }
  if (runtimeState !== 'ONLINE' && authRestorationStatus === 'SIGNED_OUT') {
    overline = 'SIGN-IN REQUIRED';
    headline = 'CONNECT TO CONTINUE.';
    description =
      'No saved sign-in could be verified on this device. Reconnect to PointCraft, then sign in before accessing business data.';
  } else if (runtimeState !== 'ONLINE' && authRestorationStatus === 'RECOVERY_REQUIRED') {
    overline = 'SIGN-IN RECOVERY';
    headline = 'CONNECT TO RESTORE YOUR SESSION.';
    description =
      'PointCraft could not verify the saved sign-in while offline. Keep this device connected and retry to restore access safely.';
  }
  if (applicationError) {
    overline = 'APPLICATION ERROR';
    headline = 'POINTCRAFT NEEDS A FRESH START.';
    description =
      'Your data is safe. Try starting the application again, or contact support if the problem continues.';
  }
  // The checks run one step at a time (device → internet → PointCraft); a step
  // after a failed one is never probed and stays "Not checked".
  let deviceStatus = deviceConnected ? 'Connected' : 'Disconnected';
  if (checkStage === 'device') deviceStatus = 'Checking';
  let internetStatus = 'Not checked';
  if (checkStage === 'internet') internetStatus = 'Checking';
  else if (internetReachable === true) internetStatus = 'Available';
  else if (internetReachable === false) internetStatus = 'Unavailable';
  let serviceStatus = 'Not checked';
  if (checkStage === 'api') serviceStatus = 'Checking';
  else if (apiReachable === true) serviceStatus = 'Available';
  else if (apiReachable === false) serviceStatus = 'Unavailable';

  const handleRetry = async () => {
    if (onApplicationRetry) {
      setApplicationRetrying(true);
      try {
        await onApplicationRetry();
      } finally {
        setApplicationRetrying(false);
      }
      return;
    }
    await connectivityCoordinator.retryNow();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        '@supports (height: 100dvh)': { minHeight: '100dvh' },
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.55fr) minmax(330px, 0.8fr)' },
        bgcolor: '#FFFFFF',
        color: '#142033',
      }}
    >
      <Box
        component="main"
        sx={{
          minWidth: 0,
          px: { xs: 3, sm: 6, lg: 9 },
          pt: { xs: 'calc(env(safe-area-inset-top) + 28px)', sm: 5, lg: 6 },
          pb: { xs: 5, sm: 6 },
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box component="img" src={logo} alt="PointCraft" sx={{ width: { xs: 190, sm: 230 } }} />

        <Box sx={{ my: 'auto', py: { xs: 6, sm: 8 } }}>
          <Typography
            variant="overline"
            sx={{ color: '#5B759B', letterSpacing: '0.2em', fontWeight: 800 }}
          >
            {overline}
          </Typography>
          <Typography
            component="h1"
            sx={{
              mt: 1.5,
              maxWidth: 760,
              fontSize: { xs: '2.45rem', sm: '3.65rem', lg: '4.65rem' },
              lineHeight: 0.98,
              letterSpacing: '-0.055em',
              fontWeight: 800,
              color: '#172235',
            }}
          >
            {headline}
          </Typography>
          <Typography
            sx={{
              mt: 3,
              maxWidth: 650,
              color: '#607089',
              fontSize: { xs: 16, sm: 18 },
              lineHeight: 1.65,
            }}
          >
            {description}
          </Typography>

          <Stack spacing={1.5} sx={{ mt: 4.5, maxWidth: 650 }}>
            <StatusRow
              icon={deviceConnected ? WifiRoundedIcon : WifiOffRoundedIcon}
              label="Device network"
              value={deviceStatus}
              healthy={deviceConnected}
              pending={checkStage === 'device'}
            />
            <StatusRow
              icon={PublicRoundedIcon}
              label="Internet access"
              value={internetStatus}
              healthy={internetReachable}
              pending={checkStage === 'internet'}
            />
            <StatusRow
              icon={DnsRoundedIcon}
              label="PointCraft status check"
              value={serviceStatus}
              healthy={apiReachable}
              pending={checkStage === 'api'}
            />
          </Stack>

          {message && !applicationError && (
            <Typography sx={{ mt: 2.5, color: '#8B4B3E', fontSize: 14 }}>{message}</Typography>
          )}
          {applicationError && (
            <Stack spacing={0.5} sx={{ mt: 2.5 }}>
              <Typography sx={{ color: '#8B4B3E', fontSize: 14 }}>{applicationError}</Typography>
              {supportReference && (
                <Typography sx={{ color: '#6F7F94', fontSize: 12 }}>
                  Support reference: {supportReference}
                </Typography>
              )}
            </Stack>
          )}

          {/* Debug Admin QA escape hatch: the simulation can only be enabled from
              Debug Admin, which is unreachable once this page has taken over. */}
          {simulatedOutage && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{
                mt: 2.5,
                px: 2,
                py: 1.25,
                maxWidth: 650,
                borderRadius: 1.5,
                bgcolor: '#FFF4E0',
                border: '1px solid #E9C892',
              }}
            >
              <Typography sx={{ color: '#7A5A20', fontSize: 14, flex: 1, minWidth: 0 }}>
                Simulated API outage active (Debug Admin QA setting).
              </Typography>
              <Button
                size="small"
                color="inherit"
                onClick={() => {
                  setSimulateFirstPartyApiOutage(false);
                  connectivityCoordinator.retryNow().catch(() => undefined);
                }}
                sx={{ color: '#7A5A20', fontWeight: 700, flexShrink: 0 }}
              >
                Turn off
              </Button>
            </Stack>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 4 }}>
            <Button
              size="large"
              variant="contained"
              disabled={checking || applicationRetrying}
              onClick={() => handleRetry().catch(() => undefined)}
              endIcon={
                checking || applicationRetrying ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <MuiIcon icon={RefreshRoundedIcon} width={20} />
                )
              }
              sx={{ minHeight: 50, px: 3, bgcolor: '#1F3150', '&:hover': { bgcolor: '#172640' } }}
            >
              {checking || applicationRetrying ? 'Checking…' : 'Try again'}
            </Button>
            {!applicationError && !pointCraftUnavailableWithInternet && (
              <Button
                size="large"
                variant="outlined"
                startIcon={<MuiIcon icon={WifiRoundedIcon} width={20} />}
                onClick={() => setWifiDialogOpen(true)}
                sx={{ minHeight: 50, borderColor: '#A7B4C6', color: '#253956' }}
              >
                Wi-Fi options
              </Button>
            )}
          </Stack>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 3 }}>
          {/* Dev-tap target: 7 taps within 3s open the boot diagnostics popup.
              Padding widens the hit area without moving the visual. */}
          <Typography
            variant="caption"
            sx={{ color: '#7C899B', py: 1, px: 1.5, mx: -1.5, my: -1 }}
            {...{ [BOOT_DIAGNOSTICS_TRIGGER_ATTRIBUTE]: '' }}
          >
            v{appVersion}
          </Typography>
        </Stack>
      </Box>

      <BootDiagnosticsHost />

      <Box
        component="aside"
        sx={{
          minWidth: 0,
          bgcolor: '#EDF2F7',
          px: { xs: 3, sm: 6, md: 5, lg: 7 },
          py: { xs: 5, md: 7 },
          display: 'flex',
          alignItems: 'stretch',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 440,
            mx: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {!applicationError && offlineShellAvailable && (
            <Button
              fullWidth
              size="large"
              variant="contained"
              onClick={() => connectivityCoordinator.enterOfflineMode().catch(() => undefined)}
              endIcon={<MuiIcon icon={ArrowForwardRoundedIcon} width={28} />}
              sx={{
                minHeight: { xs: 72, md: 84 },
                px: 4,
                borderRadius: 2,
                bgcolor: '#217A3F',
                color: '#FFFFFF',
                fontSize: { xs: 20, sm: 24 },
                fontWeight: 800,
                letterSpacing: '-0.02em',
                boxShadow: '0 12px 28px rgba(33, 122, 63, 0.28)',
                '&:hover': {
                  bgcolor: '#196634',
                  boxShadow: '0 14px 32px rgba(33, 122, 63, 0.34)',
                },
                '&:active': { bgcolor: '#14552B' },
              }}
            >
              Work offline
            </Button>
          )}

          <Box sx={{ mt: { xs: 5, md: 'auto' }, mb: { md: 'auto' }, pt: { md: 5 } }}>
            <Typography
              variant="overline"
              sx={{ color: '#5B759B', fontWeight: 800, letterSpacing: '0.16em' }}
            >
              NEED A HAND?
            </Typography>
            <Typography
              component="h2"
              sx={{
                mt: 1,
                fontSize: { xs: 30, lg: 38 },
                lineHeight: 1.1,
                fontWeight: 750,
                color: '#1B2B44',
              }}
            >
              Our support team is ready.
            </Typography>
            <Typography sx={{ mt: 2, color: '#66768C', lineHeight: 1.7 }}>
              Share what you see on this screen and we’ll help restore your connection.
            </Typography>

            <Stack spacing={1.5} sx={{ mt: 4 }}>
              <SupportLink
                href={`tel:${PLATFORM_INFO.TECHNICAL_SUPPORT_PHONE}`}
                icon={PhoneRoundedIcon}
                label="Technical support"
                value={PLATFORM_INFO.TECHNICAL_SUPPORT_PHONE}
              />
              <SupportLink
                href={`mailto:${PLATFORM_INFO.SUPPORT_EMAIL}`}
                icon={EmailRoundedIcon}
                label="Email support"
                value={PLATFORM_INFO.SUPPORT_EMAIL}
              />
            </Stack>

            <Divider sx={{ my: 4, borderColor: '#CFD8E4' }} />

            <Stack direction="row" spacing={2.5} alignItems="center">
              <Box
                component="img"
                src={supportQrCode}
                alt="PointCraft support QR code"
                sx={{ width: 92, height: 92, p: 0.75, bgcolor: 'common.white', borderRadius: 1.5 }}
              />
              <Box>
                <Typography variant="subtitle2" sx={{ color: '#1E304B' }}>
                  Scan for support
                </Typography>
                <Link
                  href={PLATFORM_INFO.SUPPORT_URL}
                  target="_blank"
                  rel="noreferrer"
                  sx={{ display: 'inline-block', mt: 0.5, color: '#4D6A91', fontSize: 14 }}
                >
                  {PLATFORM_INFO.BASE_WEBSITE_URL}
                </Link>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Box>

      <WifiConnectionDialog open={wifiDialogOpen} onClose={() => setWifiDialogOpen(false)} />
    </Box>
  );
}

type StatusRowProps = {
  icon: typeof WifiRoundedIcon;
  label: string;
  value: string;
  /** `null` means the step has not been checked yet and renders neutrally. */
  healthy: boolean | null;
  pending: boolean;
};

function StatusRow({ icon, label, value, healthy, pending }: StatusRowProps) {
  let iconBg = '#EDF1F6';
  let iconColor = '#5C6B80';
  if (!pending && healthy === true) {
    iconBg = '#E8F7ED';
    iconColor = '#277A43';
  } else if (!pending && healthy === false) {
    iconBg = '#F7ECE9';
    iconColor = '#A14E3D';
  }
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{ py: 1.25, borderBottom: '1px solid #E0E6EE' }}
    >
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: 1.25,
          display: 'grid',
          placeItems: 'center',
          bgcolor: iconBg,
          color: iconColor,
        }}
      >
        {pending ? (
          <CircularProgress size={19} color="inherit" />
        ) : (
          <MuiIcon icon={icon} width={20} />
        )}
      </Box>
      <Typography sx={{ flex: 1, color: '#52647C', fontSize: 15 }}>{label}</Typography>
      <Stack direction="row" alignItems="center" spacing={0.75}>
        {!pending && healthy === true && (
          <MuiIcon icon={CheckCircleRoundedIcon} width={17} sx={{ color: '#2C8A4C' }} />
        )}
        <Typography sx={{ color: '#22344F', fontWeight: 700, fontSize: 14 }}>{value}</Typography>
      </Stack>
    </Stack>
  );
}

type SupportLinkProps = {
  href: string;
  icon: typeof PhoneRoundedIcon;
  label: string;
  value: string;
};

function SupportLink({ href, icon, label, value }: SupportLinkProps) {
  return (
    <Link
      href={href}
      underline="none"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        borderRadius: 1.5,
        color: '#243956',
        '&:hover': { bgcolor: 'rgba(255,255,255,0.68)' },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1.25,
          display: 'grid',
          placeItems: 'center',
          bgcolor: '#DDE6F0',
        }}
      >
        <MuiIcon icon={icon} width={20} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ display: 'block', color: '#718096' }}>
          {label}
        </Typography>
        <Typography sx={{ color: '#243956', fontWeight: 700, overflowWrap: 'anywhere' }}>
          {value}
        </Typography>
      </Box>
    </Link>
  );
}
